import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useModal } from 'mui-modal-provider';
import { TextField } from '@mui/material';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { Post } from 'lib/types';
import { Job, JobApplication } from 'lib/types/jobs';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { formatLocalString } from 'lib/utils/datetime';
import { JobSelectPosts } from 'components/JobSelectPosts';
import { ResumeUpload } from './ResumeUpload';
import { DeleteApplication } from './DeleteApplication';
import { CustomAnswer } from './CustomAnswer';

interface Props {
  job: Job;
  onCancel: () => void;
}

type Error = { [key: string]: boolean | undefined };

export const ApplyForm = ({
  job, onCancel,
}: Props) => {
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useModal();

  const currentUser = useCurrentUser();

  const [application, setApplication] = useState<JobApplication | undefined>();
  const [posts, setPosts] = useState<Post[]>([]);
  const [linkedinUrl, setLinkedinUrl] = useState<string | undefined>(currentUser?.linkedinUrl);
  const [resumeUrl, setResumeUrl] = useState<string | undefined>();
  const [isLoaded, setIsLoaded] = useState(false);
  const [answers, setAnswers] = useState<{ [questionId: string]: string }>({});
  const [errors, setErrors] = useState<Error>({});
  const [contactInfo, setContactInfo] = useState<{ [key: string]: string }>({});

  const clearForm = () => {
    setPosts([]);
  };

  const changeAnswer = (questionId: number, value: string) => {
    setAnswers({
      ...answers,
      [questionId]: value,
    });
  };

  const save = async () => {
    const jobId = job.id;
    if (!jobId) {
      return;
    }

    const payload = {
      postIds: posts.map((p) => p.id),
      ...(linkedinUrl && { linkedinUrl }),
      ...(resumeUrl && { resumeUrl }),
      answers: Object.keys(answers).map(
        (k: string) => ({ questionId: Number(k), answer: answers[k] }),
      ),
      ...(Object.keys(contactInfo).length > 0 && { contactInfo }),
    };
    try {
      await API.addJobApplication(jobId, payload);
      clearForm();
      onCancel();
      enqueueSnackbar('Your application has been submitted.', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(`Error saving application: ${error}`, {
        variant: 'error',
      });
    }
  };

  const confirm = () => {
    const e: Error = {
      resume: job.resumeRequired && !resumeUrl,
    };

    job.questions?.forEach((q) => {
      if (!q.id) return;

      e[q.id.toString()] = !answers[q.id];
    });

    if (Object.values(e).find((v) => v)) {
      setErrors(e);
      return;
    }

    const modal = showModal(ConfirmationModal, {
      text: 'Are you sure you would like to submit the application?',
      onNo: () => {
        modal.destroy();
      },
      onYes: () => {
        save();
        modal.destroy();
      },
    });
  };

  useEffect(() => {
    const getJobApplication = async (jobId: number) => {
      try {
        const result = await API.getJobApplication(jobId);
        setApplication(result);
        setIsLoaded(true);
      } catch (error) {
        enqueueSnackbar(`Error loading data: ${error}`, {
          variant: 'error',
        });
      }
    };

    if (job?.id && currentUser) {
      getJobApplication(job.id);
    }
  }, [job.id, currentUser]);

  if (!currentUser) return <div>Please login to apply</div>;

  if (!isLoaded) return <div>Loading...</div>;

  if (application) {
    return (
      <>
        <h1 className="text-2xl font-semibold mb-4">Your application was submitted</h1>
        <div className="mb-4">
          You submitted an application on
          {' '}
          {formatLocalString(application.createdAt)}
          .
        </div>
        <DeleteApplication jobId={job.id} onCancel={onCancel} />
        <div className="w-full flex justify-between">
          <button
            type="button"
            className="btn-primary"
            onClick={onCancel}
          >
            Close
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <h1 className="text-xl font-bold">
        Apply for
        {' '}
        {job.name}
      </h1>
      <p className="text-sm mb-4">
        Fill out the quick application below to send
        {' '}
        {job.account.name}
        {' '}
        your profile!
      </p>
      {job.submitPosts && (
        <div className="mb-6">
          <JobSelectPosts
            posts={posts}
            onChange={setPosts}
          />
        </div>
      )}
      <div className="mb-6">
        <div className="mb-3">
          Add your LinkedIn profile
        </div>
        <TextField
          id="linkedin-url"
          fullWidth
          size="small"
          placeholder="https://www.linkedin.com/in/username/"
          onChange={(e) => setLinkedinUrl(e.target.value)}
          value={linkedinUrl}
        />
      </div>
      <div className="mb-6">
        <ResumeUpload
          onUpload={setResumeUrl}
          title={(
            <>
              {job.resumeRequired && <span className="text-red font-bold">*</span>}
              Upload your resume
            </>
          )}
        />
        {errors.resume && <div className="text-red text-sm">Resume is a required field, make sure you have clicked &apos;Upload Resume&apos; above</div>}
      </div>
      {job.collectContactInfo && (
        <div className="mb-3">
          <div className="mb-3">
            Email address
          </div>
          <TextField
            id="email"
            inputMode="email"
            label="Email"
            name="email"
            fullWidth
            size="small"
            placeholder="you@example.com"
            onChange={(e) => setContactInfo({
              ...contactInfo, email: e.target.value,
            })}
            value={contactInfo.email}
            autoComplete="email"
            type="email"
          />
          <div className="my-3">
            Phone number
          </div>
          <TextField
            id="tel"
            name="tel"
            label="Phone"
            inputMode="tel"
            fullWidth
            size="small"
            placeholder="(123) 456-7890"
            onChange={(e) => setContactInfo({
              ...contactInfo, phone: e.target.value,
            })}
            value={contactInfo.phone}
            autoComplete="tel"
            type="tel"
          />
        </div>
      )}
      {job.questions && (
        <>
          {job.questions.map((q) => {
            if (!q.id) return null;

            return (
              <CustomAnswer
                key={q.id}
                question={q}
                answer={answers[q.id]}
                error={errors[q.id]}
                onChange={changeAnswer}
              />
            );
          })}
        </>
      )}
      <div className="w-full text-right">
        <button
          type="button"
          className="btn-primary-outlined mr-2"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          className="btn-primary"
          onClick={confirm}
          type="submit"
        >
          Apply
        </button>
      </div>
    </>
  );
};
